// Super Simple Mobile Tenant Score - Even easier!
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Box, IconButton, Typography, TextField, Fade } from "@mui/material";
import { CheckCircle, Warning, Cancel, Calculate } from "@mui/icons-material";
import { useAuth } from "contexts/AuthContext";

// Client-side quick scoring logic
function quickTenantScore(creditScore, income, rent) {
  const credit = parseInt(creditScore) || 0;
  const monthlyIncome = parseFloat(income) || 0;
  const requestedRent = parseFloat(rent) || 0;

  const incomeRatio = requestedRent > 0 ? monthlyIncome / requestedRent : 0;

  if (credit >= 700 && incomeRatio >= 3) {
    return { score: "GREEN", message: "Great Tenant!" };
  } else if (credit >= 650 && incomeRatio >= 2.5) {
    return { score: "GREEN", message: "Good Candidate" };
  } else if (credit >= 600 && incomeRatio >= 2) {
    return { score: "YELLOW", message: "Proceed with Caution" };
  } else if (credit >= 550 && incomeRatio >= 1.5) {
    return { score: "YELLOW", message: "Some Risk" };
  } else {
    return { score: "RED", message: "High Risk" };
  }
}

function QuickScoreMobile({ creditScore, income, rent }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const getQuickScore = async () => {
    if (!creditScore || !income || !rent) return;

    setLoading(true);
    try {
      const scoreResult = quickTenantScore(creditScore, income, rent);
      setResult(scoreResult);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = () => {
    if (!result) return <Calculate sx={{ fontSize: 40, color: "white" }} />;

    switch (result.score) {
      case "GREEN":
        return <CheckCircle sx={{ fontSize: 60, color: "success.main" }} />;
      case "YELLOW":
        return <Warning sx={{ fontSize: 60, color: "warning.main" }} />;
      case "RED":
        return <Cancel sx={{ fontSize: 60, color: "error.main" }} />;
      default:
        return null;
    }
  };

  const getBackgroundColor = () => {
    if (!result) return "primary.main";

    switch (result.score) {
      case "GREEN":
        return "success.light";
      case "YELLOW":
        return "warning.light";
      case "RED":
        return "error.light";
      default:
        return "grey.300";
    }
  };

  return (
    <Box sx={{ textAlign: "center", py: 2 }}>
      {!result ? (
        <IconButton
          onClick={getQuickScore}
          disabled={loading || !creditScore || !income || !rent}
          sx={{
            width: 80,
            height: 80,
            backgroundColor: getBackgroundColor(),
            "&:hover": {
              transform: "scale(1.1)",
              backgroundColor: getBackgroundColor(),
            },
            transition: "all 0.3s",
          }}
        >
          {getIcon()}
        </IconButton>
      ) : (
        <Fade in={true}>
          <Box>
            {getIcon()}
            <Typography variant="h6" sx={{ mt: 1, fontWeight: "bold" }}>
              {result.message}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 2,
                color: "primary.main",
                cursor: "pointer"
              }}
              onClick={() => setResult(null)}
            >
              Tap to reset
            </Typography>
          </Box>
        </Fade>
      )}
    </Box>
  );
}

QuickScoreMobile.propTypes = {
  creditScore: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  income: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  rent: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default QuickScoreMobile;
